/**
 * MachineLearningUtils - Utility per il preprocessing e l'analisi dei dati di allenamento
 * Fornisce funzioni per preparare i dati per l'analisi ML
 */

/**
 * Preprocessa i dati di allenamento per l'analisi ML
 * @param {Object} userData - Dati utente con workouts e sets
 * @returns {Object} Dati processati con varie features estratte
 */
export function preprocessTrainingData(userData) {
    if (!userData || !userData.workouts || !userData.sets) {
      return {
        workoutFeatures: [],
        exerciseFeatures: [],
        timeSeriesData: []
      };
    }
    
    try {
      // Estrai features a livello di allenamento
      const workoutFeatures = extractWorkoutFeatures(userData.workouts, userData.sets);
      
      // Estrai features a livello di esercizio
      const exerciseFeatures = extractExerciseFeatures(userData.sets);
      
      // Crea serie temporali per analisi trend
      const timeSeriesData = createTimeSeriesData(userData.workouts, userData.sets);
      
      return {
        workoutFeatures,
        exerciseFeatures,
        timeSeriesData
      };
    } catch (error) {
      console.error('Errore nel preprocessing dei dati:', error);
      return {
        workoutFeatures: [],
        exerciseFeatures: [],
        timeSeriesData: []
      };
    }
  }
  
  /**
   * Estrae features a livello di allenamento
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente
   * @returns {Array} Features estratte per ogni allenamento
   */
  function extractWorkoutFeatures(workouts, sets) {
    // Ordina gli allenamenti per data
    const sortedWorkouts = [...workouts].sort((a, b) => 
      new Date(a.data_allenamento) - new Date(b.data_allenamento)
    );
    
    return sortedWorkouts.map(workout => {
      // Trova tutte le serie associate a questo allenamento
      const workoutSets = sets.filter(set => 
        set.allenamento_id == workout.id || 
        set.workout_id == workout.id
      );
      
      // Calcola volume totale dell'allenamento
      const totalVolume = workoutSets.reduce((sum, set) => {
        const reps = parseInt(set.ripetizioni) || 0;
        const weight = parseFloat(set.peso) || 0;
        return sum + (reps * weight);
      }, 0);
      
      // Calcola numero di esercizi unici
      const uniqueExercises = new Set(
        workoutSets.map(set => set.esercizio_id || set.esercizio_nome)
      ).size;
      
      // Calcola gruppi muscolari allenati
      const muscleGroups = new Set(
        workoutSets.map(set => set.gruppo_muscolare).filter(Boolean)
      );
      
      // Calcola intensità media (in base al peso relativo)
      const intensityData = calculateIntensityMetrics(workoutSets);
      
      // Estrai data e giorno della settimana
      const date = new Date(workout.data_allenamento);
      const dayOfWeek = date.getDay(); // 0 = domenica, 6 = sabato
      
      return {
        workoutId: workout.id,
        date: date.toISOString().split('T')[0],
        dayOfWeek,
        duration: parseInt(workout.durata_totale) || 0,
        totalVolume,
        setCount: workoutSets.length,
        uniqueExerciseCount: uniqueExercises,
        muscleGroupsCount: muscleGroups.size,
        muscleGroups: Array.from(muscleGroups),
        avgIntensity: intensityData.avgIntensity,
        maxIntensity: intensityData.maxIntensity,
        repRanges: calculateRepRanges(workoutSets)
      };
    });
  }
  
  /**
   * Calcola metriche di intensità per un insieme di serie
   * @param {Array} sets - Serie di esercizi
   * @returns {Object} Metriche di intensità
   */
  function calculateIntensityMetrics(sets) {
    if (!sets || sets.length === 0) {
      return { avgIntensity: 0, maxIntensity: 0 };
    }
    
    // Calcola intensità per ciascuna serie (usando il peso relativo come proxy)
    const intensityScores = sets.map(set => {
      const reps = parseInt(set.ripetizioni) || 0;
      if (reps <= 0) return 0;
      
      // Formula semplificata: meno ripetizioni = maggiore intensità
      // 1-5 reps: alta intensità, 6-12: media, 13+: bassa
      if (reps <= 5) return 9; // Alta intensità
      if (reps <= 8) return 7;
      if (reps <= 12) return 5;
      return 3; // Bassa intensità
    }).filter(score => score > 0);
    
    if (intensityScores.length === 0) return { avgIntensity: 0, maxIntensity: 0 };
    
    const avgIntensity = intensityScores.reduce((sum, score) => sum + score, 0) / intensityScores.length;
    const maxIntensity = Math.max(...intensityScores);
    
    return {
      avgIntensity: Math.round(avgIntensity * 10) / 10,
      maxIntensity
    };
  }
  
  /**
   * Calcola la distribuzione dei range di ripetizioni
   * @param {Array} sets - Serie di esercizi
   * @returns {Object} Distribuzione dei range di ripetizioni
   */
  function calculateRepRanges(sets) {
    if (!sets || sets.length === 0) {
      return { low: 0, medium: 0, high: 0 };
    }
    
    const ranges = {
      low: 0,    // 1-5 ripetizioni
      medium: 0, // 6-12 ripetizioni
      high: 0    // 13+ ripetizioni
    };
    
    sets.forEach(set => {
      const reps = parseInt(set.ripetizioni) || 0;
      if (reps <= 0) return;
      
      if (reps <= 5) ranges.low++;
      else if (reps <= 12) ranges.medium++;
      else ranges.high++;
    });
    
    const total = ranges.low + ranges.medium + ranges.high;
    
    // Converti in percentuali
    if (total > 0) {
      return {
        low: Math.round((ranges.low / total) * 100),
        medium: Math.round((ranges.medium / total) * 100),
        high: Math.round((ranges.high / total) * 100)
      };
    }
    
    return ranges;
  }
  
  /**
   * Estrae features a livello di esercizio
   * @param {Array} sets - Serie dell'utente
   * @returns {Array} Features estratte per ogni esercizio
   */
  function extractExerciseFeatures(sets) {
    if (!sets || sets.length === 0) return [];
    
    // Raggruppa serie per esercizio
    const exerciseGroups = {};
    
    sets.forEach(set => {
      const exerciseId = set.esercizio_id || set.scheda_esercizio_id;
      const exerciseName = set.esercizio_nome;
      
      if (!exerciseId || !exerciseName) return;
      
      const key = exerciseId.toString();
      
      if (!exerciseGroups[key]) {
        exerciseGroups[key] = {
          exerciseId,
          name: exerciseName,
          muscleGroup: set.gruppo_muscolare || 'Altro',
          sets: []
        };
      }
      
      exerciseGroups[key].sets.push({
        date: new Date(set.timestamp || set.data_allenamento || set.data || new Date()),
        reps: parseInt(set.ripetizioni) || 0,
        weight: parseFloat(set.peso) || 0,
        workoutId: set.allenamento_id || set.workout_id
      });
    });
    
    // Estrai features per ogni esercizio
    return Object.values(exerciseGroups).map(exercise => {
      // Ordina le serie per data
      exercise.sets.sort((a, b) => a.date - b.date);
      
      // Raggruppa per sessione di allenamento
      const setsByWorkout = {};
      exercise.sets.forEach(set => {
        const workoutId = set.workoutId;
        if (!workoutId) return;
        
        if (!setsByWorkout[workoutId]) {
          setsByWorkout[workoutId] = [];
        }
        
        setsByWorkout[workoutId].push(set);
      });
      
      // Calcola progressione
      const progressionData = calculateExerciseProgression(exercise.sets);
      
      // Calcola frequenza di utilizzo
      const frequencyData = calculateExerciseFrequency(exercise.sets);
      
      return {
        exerciseId: exercise.exerciseId,
        name: exercise.name,
        muscleGroup: exercise.muscleGroup,
        totalSets: exercise.sets.length,
        uniqueWorkouts: Object.keys(setsByWorkout).length,
        progression: progressionData,
        frequency: frequencyData,
        isBodyweight: exercise.sets.every(set => set.weight === 0),
        isCompound: isCompoundExercise(exercise.name),
        avgRepsPerSet: calculateAvgRepsPerSet(exercise.sets)
      };
    });
  }
  
  /**
   * Calcola la progressione di un esercizio nel tempo
   * @param {Array} sets - Serie dell'esercizio ordinate per data
   * @returns {Object} Dati di progressione
   */
  function calculateExerciseProgression(sets) {
    if (sets.length < 2) {
      return {
        totalChange: 0,
        percentChange: 0,
        slope: 0,
        plateauCount: 0
      };
    }
    
    // Calcola 1RM stimato per ciascuna serie
    const estimatedRMs = sets.map(set => {
      if (set.reps <= 0 || set.weight <= 0) return null;
      
      // Formula di Brzycki: 1RM = weight / (1.0278 - 0.0278 * reps)
      const reps = Math.min(set.reps, 20); // Limita a 20 rep per accuratezza
      const estimatedRM = set.weight / (1.0278 - 0.0278 * reps);
      
      return { date: set.date, rm: estimatedRM };
    }).filter(Boolean);
    
    if (estimatedRMs.length < 2) {
      return {
        totalChange: 0,
        percentChange: 0,
        slope: 0,
        plateauCount: 0
      };
    }
    
    // Calcola cambiamento dal primo all'ultimo allenamento
    const firstRM = estimatedRMs[0].rm;
    const lastRM = estimatedRMs[estimatedRMs.length - 1].rm;
    const totalChange = lastRM - firstRM;
    const percentChange = (totalChange / firstRM) * 100;
    
    // Calcola la pendenza (progresso medio per giorno)
    const firstDate = estimatedRMs[0].date.getTime();
    const lastDate = estimatedRMs[estimatedRMs.length - 1].date.getTime();
    const daysDiff = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
    
    const slope = daysDiff > 0 ? totalChange / daysDiff : 0;
    
    // Conta i plateau (periodi senza progressi significativi)
    let plateauCount = 0;
    let isInPlateau = false;
    let lastSignificantRM = firstRM;
    
    for (let i = 1; i < estimatedRMs.length; i++) {
      const currentRM = estimatedRMs[i].rm;
      const change = (currentRM - lastSignificantRM) / lastSignificantRM;
      
      // Se il cambiamento è meno del 2%, consideralo un plateau
      if (Math.abs(change) < 0.02) {
        if (!isInPlateau) {
          isInPlateau = true;
          plateauCount++;
        }
      } else {
        isInPlateau = false;
        lastSignificantRM = currentRM;
      }
    }
    
    return {
      totalChange: Math.round(totalChange * 10) / 10,
      percentChange: Math.round(percentChange * 10) / 10,
      slope: Math.round(slope * 1000) / 1000, // kg per giorno
      plateauCount
    };
  }
  
  /**
   * Calcola la frequenza di utilizzo di un esercizio
   * @param {Array} sets - Serie dell'esercizio ordinate per data
   * @returns {Object} Dati di frequenza
   */
  function calculateExerciseFrequency(sets) {
    if (sets.length < 2) {
      return {
        daysPerWorkout: 0,
        regularityScore: 0
      };
    }
    
    // Estrai date uniche di allenamento
    const workoutDates = [...new Set(
      sets.map(set => set.date.toISOString().split('T')[0])
    )].map(dateStr => new Date(dateStr));
    
    // Ordina date
    workoutDates.sort((a, b) => a - b);
    
    // Calcola intervalli tra allenamenti
    const intervals = [];
    for (let i = 1; i < workoutDates.length; i++) {
      const daysDiff = Math.round(
        (workoutDates[i] - workoutDates[i-1]) / (1000 * 60 * 60 * 24)
      );
      if (daysDiff > 0) intervals.push(daysDiff);
    }
    
    // Calcola media e deviazione standard degli intervalli
    if (intervals.length === 0) {
      return {
        daysPerWorkout: 0,
        regularityScore: 0
      };
    }
    
    const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
    
    // Calcola deviazione standard
    const variance = intervals.reduce((sum, int) => sum + Math.pow(int - avgInterval, 2), 0) / intervals.length;
    const stdDev = Math.sqrt(variance);
    
    // Calcola un punteggio di regolarità (inversamente proporzionale alla deviazione standard)
    // Un punteggio più alto indica allenamenti a intervalli più regolari
    // Normalizzato a 0-10
    const regularityScore = Math.max(0, Math.min(10, 10 - stdDev));
    
    return {
      daysPerWorkout: Math.round(avgInterval * 10) / 10,
      regularityScore: Math.round(regularityScore * 10) / 10
    };
  }
  
  /**
   * Determina se un esercizio è composto in base al nome
   * @param {string} exerciseName - Nome dell'esercizio
   * @returns {boolean} True se l'esercizio è composto
   */
  function isCompoundExercise(exerciseName) {
    if (!exerciseName) return false;
    
    const name = exerciseName.toLowerCase();
    const compoundExercises = [
      'squat', 'stacco', 'deadlift', 'panca', 'bench', 'press', 'military',
      'row', 'rematore', 'trazione', 'pull-up', 'dip', 'leg press'
    ];
    
    return compoundExercises.some(ex => name.includes(ex));
  }
  
  /**
   * Calcola la media di ripetizioni per serie
   * @param {Array} sets - Serie dell'esercizio
   * @returns {number} Media di ripetizioni per serie
   */
  function calculateAvgRepsPerSet(sets) {
    if (!sets || sets.length === 0) return 0;
    
    const validSets = sets.filter(set => set.reps > 0);
    if (validSets.length === 0) return 0;
    
    const totalReps = validSets.reduce((sum, set) => sum + set.reps, 0);
    return Math.round((totalReps / validSets.length) * 10) / 10;
  }
  
  /**
   * Crea dati di serie temporali per analisi trend
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente
   * @returns {Object} Dati di serie temporali
   */
  function createTimeSeriesData(workouts, sets) {
    // Raggruppa dati per data
    const dataByDate = {};
    
    // Ordina gli allenamenti per data
    const sortedWorkouts = [...workouts].sort((a, b) => 
      new Date(a.data_allenamento) - new Date(b.data_allenamento)
    );
    
    // Calcola dati giornalieri
    sortedWorkouts.forEach(workout => {
      const date = new Date(workout.data_allenamento).toISOString().split('T')[0];
      
      // Inizializza dati per questa data
      if (!dataByDate[date]) {
        dataByDate[date] = {
          date,
          workoutCount: 0,
          totalVolume: 0,
          totalSets: 0,
          muscleGroups: new Set(),
          exercises: new Set()
        };
      }
      
      dataByDate[date].workoutCount++;
      
      // Trova serie associate a questo allenamento
      const workoutSets = sets.filter(set => 
        set.allenamento_id == workout.id || 
        set.workout_id == workout.id
      );
      
      // Aggiorna statistiche giornaliere
      dataByDate[date].totalSets += workoutSets.length;
      
      workoutSets.forEach(set => {
        const reps = parseInt(set.ripetizioni) || 0;
        const weight = parseFloat(set.peso) || 0;
        
        dataByDate[date].totalVolume += reps * weight;
        
        if (set.gruppo_muscolare) {
          dataByDate[date].muscleGroups.add(set.gruppo_muscolare);
        }
        
        if (set.esercizio_nome) {
          dataByDate[date].exercises.add(set.esercizio_nome);
        }
      });
    });
    
    // Converti in array e finalizza i dati
    const timeSeriesData = Object.values(dataByDate).map(dayData => ({
      date: dayData.date,
      workoutCount: dayData.workoutCount,
      totalVolume: dayData.totalVolume,
      totalSets: dayData.totalSets,
      uniqueMuscleGroups: dayData.muscleGroups.size,
      uniqueExercises: dayData.exercises.size
    }));
    
    // Ordina per data
    timeSeriesData.sort((a, b) => a.date.localeCompare(b.date));
    
    return timeSeriesData;
  }
  
  /**
   * Estrae features per l'analisi ML
   * @param {Object} data - Dati preprocessati
   * @returns {Array} Features estratte
   */
  export function extractFeatures(data) {
    // Implementazione semplificata
    // In una versione reale, convertirebbe i dati in features compatibili con ML
    return [];
  }
  
  /**
   * Normalizza dati numerici per l'uso in modelli ML
   * @param {Array} values - Valori da normalizzare
   * @returns {Array} Valori normalizzati (0-1)
   */
  export function normalizeValues(values) {
    if (!values || values.length === 0) return [];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    if (max === min) return values.map(() => 0.5);
    
    return values.map(val => (val - min) / (max - min));
  }